// features/judge-assist/JudgeAssistChat.tsx
import { ReactElement, useEffect, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, IconButton, TextField, Typography, alpha } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import CampaignIcon from '@mui/icons-material/Campaign';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { auth } from '../../config/firebase';
import { extractUrls, hostLabel, parseResponse } from './parse';

type Role = 'user' | 'assistant';
interface Msg {
  id: number;
  role: Role;
  content: string;
  wasSearched?: boolean;
}
type FeedbackState = Record<number, { rating: 'up' | 'down'; done: boolean }>;

const EXAMPLES = [
  'A player asked me to deck check their opponent — I can see a clear pattern on the card backs',
  'A player wants to appeal my ruling. What do I do?',
  'What happens to the ongoing match during a DQ investigation?',
  'Time was just called. How does the extra turn work?',
];

// Render **bold** segments without dangerouslySetInnerHTML.
function RichText({ text }: { text: string }): ReactElement {
  return (
    <>
      {text.split('\n').map((line, i) => {
        if (!line.trim()) return <Box key={i} sx={{ height: 6 }} />;
        const parts = line.split('**');
        return (
          <Typography
            key={i}
            component="div"
            sx={{ fontSize: 13.5, lineHeight: 1.6, color: 'text.primary', mb: 0.25 }}
          >
            {parts.map((p, j) =>
              j % 2 === 1 ? (
                <Box key={j} component="strong" sx={{ fontWeight: 700 }}>
                  {p}
                </Box>
              ) : (
                <span key={j}>{p}</span>
              ),
            )}
          </Typography>
        );
      })}
    </>
  );
}

export function JudgeAssistChat(): ReactElement {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackState>({});
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async (): Promise<void> => {
    const text = input.trim();
    if (!text || loading) return;
    const userMsg: Msg = { id: Date.now(), role: 'user', content: text };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput('');
    setLoading(true);

    try {
      // Identify the user so the server can enforce the per-user daily limit.
      const token = await auth.currentUser?.getIdToken().catch(() => null);
      const res = await fetch('/api/judge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          messages: history.map((m) => ({ role: m.role, content: m.content })),
        }),
      });
      const data = await res.json();
      // Limit reached / not signed in: show the server's message as a normal
      // reply rather than the "server unreachable" template.
      if (res.status === 429 || res.status === 401) {
        setMessages((prev) => [
          ...prev,
          { id: Date.now() + 1, role: 'assistant', content: data?.error || 'Judge Assist is unavailable right now.' },
        ]);
        return;
      }
      if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, role: 'assistant', content: data.text, wasSearched: data.wasSearched },
      ]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'assistant',
          content:
            '**IMMEDIATE ACTION**\nUnable to reach Judge Assist. Make sure the API server is running (`npm run judge:server`).\n\n**GUIDANCE**\nIf it persists, consult the TRM directly or call your Head Judge.\n\n**REFERENCE**\nError: ' +
            message,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const rate = async (msg: Msg, rating: 'up' | 'down'): Promise<void> => {
    setFeedback((prev) => ({ ...prev, [msg.id]: { rating, done: true } }));
    const idx = messages.findIndex((m) => m.id === msg.id);
    const query = idx > 0 ? messages[idx - 1].content : '';
    try {
      await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, response: msg.content, rating }),
      });
    } catch {
      /* non-blocking: feedback is best-effort */
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', px: 1, pt: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>Ready to assist</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2, lineHeight: 1.6 }}>
              Ask about rulings, penalties, deck checks, DQ procedure, appeals, or card interactions.
            </Typography>
            {EXAMPLES.map((ex) => (
              <Box
                key={ex}
                onClick={() => setInput(ex)}
                sx={{
                  textAlign: 'left',
                  fontSize: 12,
                  lineHeight: 1.5,
                  color: 'text.secondary',
                  p: 1.25,
                  mb: 1,
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.card',
                  '&:hover': { borderColor: 'primary.main', color: 'text.primary' },
                }}
              >
                {ex}
              </Box>
            ))}
          </Box>
        )}

        {messages.map((m) => {
          if (m.role === 'user') {
            return (
              <Box key={m.id} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.25 }}>
                <Box
                  sx={{
                    maxWidth: '85%',
                    px: 1.5,
                    py: 1,
                    borderRadius: '14px 14px 4px 14px',
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: 'primary.contrastText',
                    backgroundColor: 'primary.main',
                  }}
                >
                  {m.content}
                </Box>
              </Box>
            );
          }

          const parsed = parseResponse(m.content);
          const urls = m.wasSearched ? extractUrls(m.content) : [];
          const fb = feedback[m.id];

          return (
            <Box key={m.id} sx={{ mb: 2 }}>
              <Box
                sx={{
                  borderRadius: '4px 14px 14px 14px',
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                }}
              >
                {parsed.immediate && (
                  <Box
                    sx={{
                      mb: 1.25,
                      p: 1.25,
                      borderRadius: 2,
                      backgroundColor: (t) => alpha(t.palette.error.main, 0.1),
                      border: '1px solid',
                      borderColor: (t) => alpha(t.palette.error.main, 0.4),
                      borderLeft: '3px solid',
                      borderLeftColor: 'error.main',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 0.5,
                        color: 'error.main',
                        fontSize: 9.5,
                        fontWeight: 800,
                        letterSpacing: 1.4,
                      }}
                    >
                      <BoltIcon sx={{ fontSize: 13 }} /> IMMEDIATE ACTION
                    </Box>
                    <Typography sx={{ fontSize: 13.5, lineHeight: 1.5, fontWeight: 500 }}>
                      {parsed.immediate}
                    </Typography>
                  </Box>
                )}

                {parsed.escalation && (
                  <Box
                    sx={{
                      mb: 1.25,
                      p: 1.25,
                      borderRadius: 2,
                      backgroundColor: (t) => alpha(t.palette.secondary.main, 0.12),
                      border: '1px solid',
                      borderColor: (t) => alpha(t.palette.secondary.main, 0.45),
                      borderLeft: '3px solid',
                      borderLeftColor: 'secondary.main',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 0.5,
                        color: 'secondary.main',
                        fontSize: 9.5,
                        fontWeight: 800,
                        letterSpacing: 1.4,
                      }}
                    >
                      <CampaignIcon sx={{ fontSize: 14 }} /> ESCALATE
                    </Box>
                    <Typography sx={{ fontSize: 13, lineHeight: 1.5, fontWeight: 500 }}>
                      {parsed.escalation}
                    </Typography>
                  </Box>
                )}

                {parsed.guidance && <RichText text={parsed.guidance} />}

                {parsed.reference && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.75,
                      mt: 1.25,
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: 'background.card',
                      borderLeft: '2px solid',
                      borderLeftColor: 'info.main',
                    }}
                  >
                    <Box sx={{ color: 'info.main', fontWeight: 800, fontSize: 12 }}>§</Box>
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.5 }}>
                      {parsed.reference}
                    </Typography>
                  </Box>
                )}

                {m.wasSearched && (
                  <Chip
                    icon={<SearchIcon sx={{ fontSize: 13 }} />}
                    label="Web search used — verify sources"
                    size="small"
                    sx={{
                      mt: 1.25,
                      height: 22,
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'warning.main',
                      backgroundColor: (t) => alpha(t.palette.warning.main, 0.12),
                      '& .MuiChip-icon': { color: 'warning.main' },
                    }}
                  />
                )}

                {urls.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {urls.map((u) => (
                      <Chip
                        key={u}
                        component="a"
                        href={u}
                        target="_blank"
                        rel="noreferrer"
                        clickable
                        size="small"
                        deleteIcon={<OpenInNewIcon />}
                        onDelete={() => window.open(u, '_blank', 'noreferrer')}
                        label={hostLabel(u)}
                        sx={{ height: 22, fontSize: 10, color: 'info.light' }}
                      />
                    ))}
                  </Box>
                )}

                {/* Feedback — seeds the verified-ruling store */}
                <Box
                  sx={{
                    mt: 1.25,
                    pt: 1,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {fb?.done ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: 11,
                        color: 'success.main',
                      }}
                    >
                      <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> Feedback recorded
                    </Box>
                  ) : (
                    <>
                      <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mr: 0.5 }}>
                        Was this correct?
                      </Typography>
                      <IconButton size="small" onClick={() => rate(m, 'up')} sx={{ color: 'success.main' }}>
                        <ThumbUpAltOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => rate(m, 'down')} sx={{ color: 'error.main' }}>
                        <ThumbDownAltOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', px: 0.5 }}>
            <CircularProgress size={14} thickness={6} />
            <Typography sx={{ fontSize: 12 }}>Consulting rules…</Typography>
          </Box>
        )}
        <div ref={bottomRef} />
      </Box>

      {/* Input */}
      <Box
        sx={{
          px: 1.25,
          pt: 1.25,
          // clear the mobile home bar when the panel is anchored to the bottom
          pb: 'calc(env(safe-area-inset-bottom, 0px) + 10px)',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
        }}
      >
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder="Describe the situation…"
          size="small"
          fullWidth
          multiline
          maxRows={4}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, backgroundColor: 'background.card' } }}
        />
        <IconButton
          onClick={() => void send()}
          disabled={loading || !input.trim()}
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
          }}
        >
          <ArrowUpwardIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
